import {
  foodCompanyOptions,
  snackSupplierOptions,
  updateCompanyFoodDeals,
} from "@/api";
import PageBackButton from "@/components/PageBackButton";
import { useFoodDeals } from "@/hooks/useFoodDeals";
import { useWelcomeFoodAccess } from "@/hooks/useWelcomeFoodAccess";
import {
  Breadcrumbs,
  Button,
  Chip,
  Icon,
  InputField,
  LoadingSpinner,
  ProgressIndicator,
  StrictNumberInput,
} from "@app-components";
import { FieldApi, useForm } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "@/styles/Food.module.scss";
import {
  getBuildingId,
  foodDealLimitLabelStyles,
  notifyApiError,
  NOK,
} from "@/lib";
import { reassignDefaultAfterRemoval } from "@/lib/food";
import { AnimatePresence, motion } from "framer-motion";
import { usePrompt } from "@/hooks/usePrompt";
import SaveResetButtons from "@/components/SaveResetButtons";
import { useRouterStore, useSnackbarStore } from "@/store/zustandStore";
import {
  CompanyFoodDeal,
  FoodCompany,
  FoodDeal,
  NotificationType,
} from "@/types";
import CompanyUserStats from "@/components/company_admin/company_settings/food/FoodUsersStats";
import { AnimatedTickingNumber } from "@/components/AnimatedTickingNumber";
import { useBuildingQuery } from "@/components/RouterSetup";

const fromRight = {
  initial: { x: 40, opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: 40, opacity: 0 },
};

const fromLeft = {
  initial: { x: -40, opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: -40, opacity: 0 },
};

const slideFade = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 10 },
  layout: true,
};

const buildingDealsChipStyles = {
  fontSize: "var(--wlcm-text-sm)",
  background: "rgba(0,0,0,0.1)",
};

export const Route = createFileRoute("/_portal/lunch-admin/deals/$companyId")({
  component: RouteComponent,
  loader: ({ params }) => {
    const { queryClient, supplierCompanyId } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.prefetchQuery(foodCompanyOptions(params.companyId));
    queryClient.prefetchQuery(snackSupplierOptions(supplierCompanyId));
  },
});

function RouteComponent() {
  const { allowFoodAdminAccess } = useWelcomeFoodAccess();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addNotification } = useSnackbarStore();
  const { companyId } = Route.useParams();
  const { data: company, isLoading } = useBuildingQuery(
    foodCompanyOptions(companyId),
  );
  const { deals, isLoading: isLoadingDeals } = useFoodDeals();
  const [isEditingCredit, setIsEditingCredit] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const creditInputBlurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  usePrompt({
    isOpen: hasUnsavedChanges,
  });

  const breadcrumbs = [
    { label: t("SIDEBAR.FOOD_DEALS"), path: "/lunch-admin/deals" },
    {
      label: company?.buildingTenantName ?? t("SIDEBAR.WORKSPACES.COMPANY"),
      path: `/lunch-admin/deals/${companyId}`,
    },
  ];

  const form = useForm({
    defaultValues: {
      foodDeals: company?.foodDeals || [],
      lunchCreditAmount: company?.lunchCreditAmount || 0,
    },
    onSubmit: async ({ value }) => {
      try {
        await updateCompanyFoodDeals(
          companyId,
          value.foodDeals,
          value.lunchCreditAmount,
        );
        setHasUnsavedChanges(false);
        queryClient.invalidateQueries({
          queryKey: [getBuildingId(), "food-company", companyId],
        });
        queryClient.invalidateQueries({
          queryKey: [getBuildingId(), "food-companies"],
        });
        addNotification(
          t("NOTIFICATIONS.DOCUMENT_UPDATED", {
            entity: company?.buildingTenantName,
          }),
          NotificationType.SUCCESS,
        );
      } catch (error) {
        notifyApiError(error, addNotification, t, {
          properties: { entity: company?.buildingTenantName },
          fallbackKey: "UPDATE_FAILED",
        });
      }
    },
  });

  if (!allowFoodAdminAccess) return null;
  return (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton
          fallbackPath={{
            to: "/lunch-admin/deals",
          }}
        />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <div className="pageHeader">
        <h1>
          {t("SIDEBAR.WORKSPACES.COMPANY")}
          {company && (
            <span
              style={{ opacity: 0.5, marginLeft: "var(--wlcm-spacing-sm)" }}
            >
              {" "}
              {company.buildingTenantName}
            </span>
          )}
        </h1>
      </div>
      {isLoading ? (
        <div className="center">
          <LoadingSpinner />
        </div>
      ) : deals.length === 0 ? (
        <div
          style={{
            margin: "var(--wlcm-spacing-xl) auto 0 auto",
            opacity: 0.5,
            textAlign: "center",
            fontSize: "var(--wlcm-text-lg)",
          }}
        >
          <p>
            <Icon
              name="handshake"
              style={{
                fontSize: "var(--wlcm-text-xl)",
                marginBottom: "var(--wlcm-spacing-sm)",
              }}
            />
            <br />
            {t("FOOD_ADMIN.DEALS.NO_DEALS")}
            <br />
            <span style={{ fontSize: "var(--wlcm-text-default)" }}>
              {t("FOOD_ADMIN.DEALS.NO_DEALS_INFO")}
            </span>
          </p>
        </div>
      ) : (
        company && (
          <>
            <CompanyUserStats
              foodCompany={company}
              style={{ marginBottom: "var(--wlcm-spacing-md)" }}
            />
            <form
              onChange={() => setHasUnsavedChanges(true)}
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
              }}
            >
              <form.Field name="foodDeals">
                {(field) => {
                  const leftoverDeals = [...deals]
                    .filter(
                      (deal) =>
                        !field.state.value.some(
                          (cd) => cd.dealId === deal.dealId,
                        ),
                    )
                    .reverse();

                  const filteredLeftoverDeals = searchQuery
                    ? leftoverDeals.filter((deal) =>
                        deal.name
                          ?.toLowerCase()
                          .includes(searchQuery.toLowerCase()),
                      )
                    : leftoverDeals;

                  return (
                    <div className={styles.companyDealsContainer}>
                      <div className={styles.dealsAndCreditContainer}>
                        <section
                          className={`${styles.addedDealsSection} ${!company.foodDeals?.length ? styles.noDeals : ""}`}
                        >
                          <h3>{t("FOOD_ADMIN.DEALS.COMPANY_DEALS")}</h3>
                          {field.state.value.length >
                            (company.foodDeals ?? []).length && (
                            <div className="banner warningBanner">
                              <Icon name="warning" />
                              <p>
                                <strong>
                                  {t("FOOD_ADMIN.DEALS.WARNING_BANNER_TITLE")}
                                </strong>{" "}
                                {t("FOOD_ADMIN.DEALS.WARNING_BANNER_TEXT")}
                              </p>
                            </div>
                          )}
                          {isLoadingDeals ? (
                            <div className={styles.placeholder}>
                              <LoadingSpinner />
                            </div>
                          ) : field.state.value.length === 0 ? (
                            <p className={styles.placeholder}>
                              <Icon name="handshake" />
                              <br />
                              {t("FOOD_ADMIN.DEALS.NO_DEALS_ADDED")}
                            </p>
                          ) : (
                            <AnimatePresence initial={false}>
                              {field.state.value.map((deal) => (
                                <CompanyDealCard
                                  key={deal.dealId}
                                  deal={deal}
                                  field={field}
                                  deals={deals}
                                  company={company}
                                  setHasUnsavedChanges={setHasUnsavedChanges}
                                />
                              ))}
                            </AnimatePresence>
                          )}
                        </section>
                        <section className={styles.creditSection}>
                          <h3>{t("FOOD_ADMIN.DEALS.LUNCH_CREDIT.HEADER")}</h3>
                          <p>
                            {t("FOOD_ADMIN.DEALS.LUNCH_CREDIT.DESCRIPTION")}
                          </p>
                          <form.Field name="lunchCreditAmount">
                            {(field) => {
                              const addingValueForFirstTime = !!(
                                company.lunchCreditAmount === undefined &&
                                field.state.value
                              );
                              return (
                                <>
                                  <AnimatePresence initial={false} mode="wait">
                                    {isEditingCredit ? (
                                      <motion.div
                                        key="edit"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                      >
                                        <StrictNumberInput
                                          compact
                                          allowUndefined
                                          autoFocus
                                          label={t(
                                            "FOOD_ADMIN.DEALS.LUNCH_CREDIT.LABEL",
                                          )}
                                          value={field.state.value}
                                          onChange={(e) =>
                                            field.handleChange(e.target.value)
                                          }
                                          onFocus={() => {
                                            if (
                                              creditInputBlurTimeoutRef.current
                                            ) {
                                              clearTimeout(
                                                creditInputBlurTimeoutRef.current,
                                              );
                                              creditInputBlurTimeoutRef.current =
                                                null;
                                            }
                                            setIsEditingCredit(true);
                                          }}
                                          onBlur={() => {
                                            creditInputBlurTimeoutRef.current =
                                              setTimeout(() => {
                                                setIsEditingCredit(false);
                                              }, 100);
                                          }}
                                          min={0}
                                          max={10_000}
                                        />
                                      </motion.div>
                                    ) : (
                                      <motion.div
                                        key="display"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                      >
                                        <p
                                          style={
                                            !field.state.value
                                              ? { color: "black", opacity: 0.5 }
                                              : {}
                                          }
                                        >
                                          {field.state.value ? (
                                            <AnimatedTickingNumber
                                              to={field.state.value}
                                              formatter={NOK.format}
                                            />
                                          ) : (
                                            t(
                                              "FOOD_ADMIN.DEALS.LUNCH_CREDIT.NO_CREDIT",
                                            )
                                          )}
                                        </p>
                                        {
                                          /* For first iteration, we only allow setting the credit once - no editing after a value is set */
                                          company.lunchCreditAmount ===
                                            undefined && (
                                            <Button
                                              secondary
                                              blue
                                              icon="edit"
                                              onClick={() =>
                                                setIsEditingCredit(true)
                                              }
                                            >
                                              {t(
                                                "FOOD_ADMIN.DEALS.LUNCH_CREDIT.EDIT",
                                              )}
                                            </Button>
                                          )
                                        }
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                  {(addingValueForFirstTime ||
                                    company.lunchCreditAmount) && (
                                    <div
                                      className={`highlightedInfo ${addingValueForFirstTime ? "warning" : ""}`}
                                    >
                                      <Icon
                                        name={
                                          addingValueForFirstTime
                                            ? "warning"
                                            : "info"
                                        }
                                      />
                                      <p>
                                        {addingValueForFirstTime && (
                                          <strong>
                                            {t(
                                              "FOOD_ADMIN.DEALS.LUNCH_CREDIT.WARNING_BOLD",
                                            )}
                                          </strong>
                                        )}
                                        {t(
                                          "FOOD_ADMIN.DEALS.LUNCH_CREDIT.WARNING_TEXT",
                                        )}
                                      </p>
                                    </div>
                                  )}
                                </>
                              );
                            }}
                          </form.Field>
                        </section>
                      </div>
                      <section className={styles.availableDealsSection}>
                        <h3>{t("FOOD_ADMIN.DEALS.OTHER_DEALS")}</h3>
                        {leftoverDeals.length > 6 && (
                          <InputField
                            compact
                            icon="search"
                            label={t("SEARCH")}
                            hideLabel
                            placeholder={`${t("SEARCH")}...`}
                            value={searchQuery}
                            onChange={(e) => {
                              e.stopPropagation();
                              setSearchQuery(e.target.value);
                            }}
                            onClear={() => setSearchQuery("")}
                          />
                        )}
                        {isLoadingDeals ? (
                          <div className={styles.placeholder}>
                            <LoadingSpinner />
                          </div>
                        ) : leftoverDeals.length === 0 ? (
                          <p className={styles.placeholder}>
                            <Icon name="handshake" />
                            <br />
                            {t("FOOD_ADMIN.DEALS.NO_MORE_DEALS")}
                          </p>
                        ) : filteredLeftoverDeals.length === 0 ? (
                          <p className={styles.placeholder}>
                            <Icon name="search" />
                            <br />
                            {t("FOOD_ADMIN.DEALS.NO_MATCHING_DEALS")}
                          </p>
                        ) : (
                          <AnimatePresence initial={false}>
                            {filteredLeftoverDeals.map((deal) => (
                              <motion.div
                                key={deal.dealId}
                                variants={fromLeft}
                                initial={false}
                                animate="animate"
                                exit="exit"
                                layout
                                className={styles.card}
                                role="button"
                                tabIndex={0}
                                aria-label="Add deal to company"
                                onClick={() => {
                                  const canBeDefault = !field.state.value.some(
                                    (d) => d.default,
                                  );
                                  field.handleChange([
                                    {
                                      dealId: deal.dealId,
                                      ...(deal.isGuest
                                        ? { isGuestDeal: true }
                                        : {}),
                                      ...(canBeDefault
                                        ? { default: true }
                                        : {}),
                                    },
                                    ...field.state.value,
                                  ]);
                                  setHasUnsavedChanges(true);
                                }}
                              >
                                <p
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: "var(--wlcm-spacing-xs) var(--wlcm-spacing-sm)",
                                  }}
                                >
                                  <strong>{deal.name}</strong>
                                  <span>{NOK.format(deal.price)}</span>
                                  {deal.isGuest && (
                                    <Chip
                                      label={t("FOOD_ADMIN.DEALS.GUEST")}
                                      style={buildingDealsChipStyles}
                                    />
                                  )}
                                  {!!deal.subsidyPercentage && (
                                    <Chip
                                      label={`${deal.subsidyPercentage}% ${t("FOOD_ADMIN.DEALS.SUBSIDY").toLowerCase()}`}
                                      style={buildingDealsChipStyles}
                                    />
                                  )}
                                </p>
                                <Icon
                                  name="add"
                                  style={{
                                    color: "var(--wlcm-color-malling-purple)",
                                  }}
                                />
                              </motion.div>
                            ))}
                          </AnimatePresence>
                        )}
                      </section>
                    </div>
                  );
                }}
              </form.Field>
              <SaveResetButtons
                form={form}
                showButtons={hasUnsavedChanges}
                onHideButtons={() => setHasUnsavedChanges(false)}
                disabledSubmit={!hasUnsavedChanges}
              />
            </form>
          </>
        )
      )}
    </>
  );
}

const CompanyDealCard = ({
  deal,
  field,
  deals,
  company,
  setHasUnsavedChanges,
}: {
  deal: CompanyFoodDeal;
  field: FieldApi<
    {
      foodDeals: CompanyFoodDeal[];
      lunchCreditAmount: number;
    },
    "foodDeals",
    undefined,
    undefined,
    CompanyFoodDeal[]
  >;
  deals: FoodDeal[];
  company: FoodCompany;
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
}) => {
  const { t } = useTranslation();
  const foundDeal = deals.find((d) => d.dealId === deal.dealId);
  const isGuestDeal = foundDeal?.isGuest ?? deal.isGuestDeal;
  const justAdded = !company.foodDeals?.some((cd) => cd.dealId === deal.dealId);
  const [isEditingLimit, setIsEditingLimit] = useState(false);
  const blockInputDismissal = useRef(false);

  const handleRemoveDeal = () => {
    const newDeals = reassignDefaultAfterRemoval(field.state.value, deal);
    field.handleChange(newDeals);
    setHasUnsavedChanges(true);
  };

  return (
    <motion.div
      key={deal.dealId}
      variants={fromRight}
      initial={false}
      animate="animate"
      exit="exit"
      layout
      className={`${styles.card} ${justAdded ? styles.justAdded : ""}`}
      {...(justAdded
        ? {
            role: "button",
            "aria-label": "Remove deal",
            tabIndex: 0,
          }
        : {})}
      onClick={() => {
        if (!justAdded) return;
        handleRemoveDeal();
      }}
      onKeyDown={(e) => {
        if (!justAdded) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleRemoveDeal();
        }
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "var(--wlcm-spacing-xs) var(--wlcm-spacing-sm)",
        }}
      >
        {deal.default ? (
          <Chip
            icon="star"
            label={t("ACCESS_GROUPS.DEFAULT")}
            className={styles.defaultChip}
          />
        ) : (
          <Button
            secondary
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              const newDeals = field.state.value.map((cd) => ({
                ...cd,
                default: cd.dealId === deal.dealId,
              }));
              field.handleChange(newDeals);
              setHasUnsavedChanges(true);
            }}
          >
            {t("FOOD_ADMIN.DEALS.MAKE_DEFAULT")}
          </Button>
        )}
        {isGuestDeal && <Chip label={t("FOOD_ADMIN.DEALS.GUEST")} />}
        <p>
          <strong>{foundDeal?.name || "Unknown Deal"}</strong>{" "}
          {NOK.format(foundDeal?.price || 0)}
        </p>
      </div>
      {justAdded && <Icon name="subtract" className={styles.removeIcon} />}
      <div className={styles.progressContainer}>
        <AnimatePresence initial={false} mode="wait">
          {isGuestDeal ? (
            <motion.p
              key="guest"
              {...slideFade}
              style={foodDealLimitLabelStyles(
                company?.usersCountPerDeal?.[deal.dealId],
              )}
            >
              {t("FOOD_ADMIN.DEALS.USERS_USING_NO_LIMIT", {
                value: company?.usersCountPerDeal?.[deal.dealId] || 0,
              })}
            </motion.p>
          ) : justAdded || isEditingLimit ? (
            <motion.div key="input" {...slideFade}>
              <StrictNumberInput
                compact
                autoFocus
                label={t("FOOD_ADMIN.DEALS.MAX_USERS")}
                value={deal.maxUsers}
                allowUndefined
                onChange={(e) => {
                  const newLimit = e.target.value || undefined;
                  if (newLimit === deal.maxUsers) return;
                  const newDeals = field.state.value.map((cd) =>
                    cd.dealId === deal.dealId
                      ? {
                          ...cd,
                          maxUsers: newLimit,
                        }
                      : cd,
                  );
                  field.handleChange(newDeals);
                  setHasUnsavedChanges(true);
                }}
                onFocus={() => setIsEditingLimit(true)}
                onBlur={() =>
                  setTimeout(() => {
                    if (!blockInputDismissal.current) setIsEditingLimit(false);
                  }, 100)
                }
                onButtonClick={(e) => {
                  e.stopPropagation();
                  blockInputDismissal.current = true;
                  setTimeout(() => {
                    blockInputDismissal.current = false;
                  }, 100);
                }}
                onClick={(e) => e.stopPropagation()}
                min={1}
                max={10_000}
                placeholder={t("FOOD_ADMIN.DEALS.UNLIMITED")}
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "4px var(--wlcm-spacing-sm)",
                  width: "max-content",
                }}
                inputStyles={{ minWidth: 75 }}
              />
            </motion.div>
          ) : deal.maxUsers ? (
            <motion.div key="progress" {...slideFade}>
              <ProgressIndicator
                id={`user-in-deal--${deal.dealId}`}
                label={(values) => (
                  <>
                    <span
                      style={foodDealLimitLabelStyles(
                        values.value,
                        values.percentage,
                      )}
                    >
                      {t("FOOD_ADMIN.DEALS.USERS_USING", values)}
                    </span>
                    <button
                      type="button"
                      className={styles.limitButton}
                      onClick={() => setIsEditingLimit(true)}
                    >
                      {t("FOOD_ADMIN.DEALS.CHANGE_LIMIT")}
                    </button>
                  </>
                )}
                value={company.usersCountPerDeal?.[deal.dealId] || 0}
                total={deal.maxUsers || company.integratedUsersCount}
                trackHeight={company.usersCountPerDeal?.[deal.dealId] ? 8 : 0}
                trackColor="transparent"
                barColor={(percentage) =>
                  percentage <= 100
                    ? "var(--wlcm-color-success-green)"
                    : "var(--wlcm-color-danger-red)"
                }
              />
            </motion.div>
          ) : (
            <motion.div
              key="no-limit"
              {...slideFade}
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "var(--wlcm-spacing-xs)",
              }}
            >
              <p
                style={foodDealLimitLabelStyles(
                  company?.usersCountPerDeal?.[deal.dealId],
                )}
              >
                {t("FOOD_ADMIN.DEALS.USERS_USING_NO_LIMIT", {
                  value: company?.usersCountPerDeal?.[deal.dealId] || 0,
                })}
              </p>
              <button
                type="button"
                className={styles.limitButton}
                onClick={() => setIsEditingLimit(true)}
              >
                {t("FOOD_ADMIN.DEALS.SET_LIMIT")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
